import { useEffect, useImperativeHandle } from 'react';

import { InpockInput, Rules, Select } from 'inpock-ui-react';

import { CashReceiptUsage, 현금영수증_발행용도 } from '@/apis';
import { useForm } from '@/hooks';
import { CASH_RECEIPT_BUSINESS, CASH_RECEIPT_CARD, CUSTOMER_PHONE } from '@/constants';
import {
  CASH_RECEIPT_USAGE,
  CashReceiptFormProps,
} from '@/fragments/checkout/sections/CashReceiptSection';
import { mapCashReceiptUsageToE, mapEToCashReceiptUsage } from '@/utils';

import { useSectionContext } from '../SectionContext';

import * as S from './styled';

export function CashReceiptForm(props: CashReceiptFormProps) {
  const {
    form: cashReceiptForm,
    setForm,
    cashReceiptUsage,
    setCashReceiptUsage,
    cashReceiptTypeSelected,
    setCashReceiptTypeSelected,
  } = props;
  const { cashReceiptSectionRef } = useSectionContext();

  const isCashReceiptTypePersonal = cashReceiptTypeSelected === 'personal_phone';
  const { form, register, inputRef } = useForm(cashReceiptForm);

  useImperativeHandle(cashReceiptSectionRef, () => ({
    ...inputRef.current,
  }));

  const onCashReceiptUsageChange = (e: string) => {
    const newValue = mapEToCashReceiptUsage(e);

    setCashReceiptUsage(newValue);

    /* 지출 증빙용으로 변경될 경우 신청정보 비지니스 넘버로 변경
     * 소득 공제일 경우, 고객 전화번호로 변경
     */
    if (newValue === false) {
      setCashReceiptTypeSelected('business_num');
    } else if (newValue === true) {
      setCashReceiptTypeSelected('personal_phone');
    }

    register('cash_receipt_number').onInput('', 'cash_receipt_number');
    register('cash_receipt_number').onInvalid('', 'cash_receipt_number');
  };

  const transformedCashReceiptUsage = Object.entries(CASH_RECEIPT_USAGE).map(([label, value]) => ({
    label,
    value,
  }));

  useEffect(() => {
    setForm({ ...form });
  }, [form, setForm]);

  const cashReceiptUsageToSelectValue = (usage: 현금영수증_발행용도): string => {
    return mapCashReceiptUsageToE(usage);
  };

  return (
    <>
      <Select
        value={cashReceiptUsageToSelectValue(cashReceiptUsage)}
        options={transformedCashReceiptUsage}
        onInput={(e) => onCashReceiptUsageChange(e)}
        required
        width="100%"
      />
      {CashReceiptUsage.미발행 !== cashReceiptUsage && (
        <>
          <InpockInput
            ref={(el) => (inputRef.current['cash_receipt_number'] = el)}
            width="100%"
            {...register('cash_receipt_number')}
            value={form.cash_receipt_number ?? ''}
            type="numeric"
            maxLength={isCashReceiptTypePersonal ? 11 : 19}
            guide="현금영수증 신청정보"
            required
            placeholder={
              CashReceiptUsage.소득공제용 === cashReceiptUsage
                ? isCashReceiptTypePersonal
                  ? '01000000000'
                  : '카드번호'
                : '사업자번호'
            }
            validator={(_rules, value) => {
              if (CashReceiptUsage.소득공제용 === cashReceiptUsage) {
                if (isCashReceiptTypePersonal) {
                  return CUSTOMER_PHONE.validator(CUSTOMER_PHONE.rules as Rules, value);
                }

                return CASH_RECEIPT_CARD.validator(CASH_RECEIPT_CARD.rules as Rules, value);
              } else {
                return CASH_RECEIPT_BUSINESS.validator(CASH_RECEIPT_BUSINESS.rules as Rules, value);
              }
            }}
            rules={
              CashReceiptUsage.소득공제용 === cashReceiptUsage
                ? isCashReceiptTypePersonal
                  ? CUSTOMER_PHONE.rules
                  : CASH_RECEIPT_CARD.rules
                : CASH_RECEIPT_BUSINESS.rules
            }
            prefix={
              CashReceiptUsage.소득공제용 === cashReceiptUsage ? (
                <Select
                  className="mb-0"
                  width="100%"
                  style={{ minWidth: '130px' }}
                  options={[
                    { label: '전화번호', value: 'personal_phone' },
                    { label: '현금영수증 카드', value: 'personal_card' },
                  ]}
                  value={cashReceiptTypeSelected}
                  onInput={(v) => {
                    setCashReceiptTypeSelected(v);
                    register('cash_receipt_number').onInput('', 'cash_receipt_number');
                    register('cash_receipt_number').onInvalid('', 'cash_receipt_number');
                  }}
                />
              ) : undefined
            }
          />
          <S.CashReceiptGuideContainer>
            <S.CashReceiptGuideItem>
              <div>*</div>
              <p>
                존재하지 않는 핸드폰 번호나 카드번호를 입력했을 경우 자동으로 자진발급처리 됩니다.
              </p>
            </S.CashReceiptGuideItem>
            <S.CashReceiptGuideItem>
              <div>*</div>
              <p>
                발급된 현금영수증은{' '}
                <span className="text-bold">
                  주문내역 {'>'} 주문상세보기 {'>'} 현금영수증
                </span>{' '}
                및 <span className="text-bold">국세청</span>에서 확인하실 수 있습니다.
              </p>
            </S.CashReceiptGuideItem>
          </S.CashReceiptGuideContainer>
        </>
      )}
    </>
  );
}
